import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { reloadForMaintenance, confirmMaintenanceThenReload } from '../api/client';
import { isMaintenanceActive } from '../utils/maintenanceReload';

/**
 * Router-level maintenance guard (FR-137, FR-138).
 *
 * The API interceptor already reloads onto the nginx maintenance page when an
 * API call returns 503. But an already-loaded SPA also does client-side route
 * navigation that makes no API call, so before this guard a visitor could keep
 * browsing a stale app after maintenance started and only hit a (generic-looking)
 * error later. This guard runs on every in-app navigation and surfaces the
 * static maintenance page on the visitor's NEXT navigation of any kind:
 *
 *  - Fast path: if a prior 503 already marked maintenance active this session,
 *    reload immediately so we don't flash the stale destination page.
 *  - Confirm: `confirmMaintenanceThenReload` HEAD-probes the origin root (nginx
 *    returns 503 for EVERY route while the flag file exists, see PRODUCTION.md
 *    "Maintenance Mode"), so it detects maintenance even on the first navigation
 *    after it starts — and clears the marker once the site is back, so the app
 *    self-heals.
 *
 * The same `confirmMaintenanceThenReload` runs from the API 503 interceptor, so
 * a same-page action (e.g. pressing Login) and an in-app navigation surface the
 * maintenance page through one shared, loop-safe path.
 *
 * Must be rendered inside the router (it uses `useLocation`).
 */
export function MaintenanceGuard(): null {
  const location = useLocation();

  useEffect(() => {
    // Fast path — we already know maintenance is on (guarded to once/30s).
    if (isMaintenanceActive()) {
      reloadForMaintenance();
    }
    // Confirm against the server; surfaces (or clears) maintenance on its own.
    confirmMaintenanceThenReload();
  }, [location.pathname]);

  return null;
}
